/**
 * What the site tells whoever watches it in production.
 *
 * Error reporting is switched on by REACT_APP_SENTRY_DSN at build time and
 * the SDK is loaded only then, in its own chunk, so a deployment without it
 * ships nothing extra. Events lose request bodies, URL queries/fragments
 * (guest/login tokens travel there), headers and user contact fields before
 * delivery. Callers must keep sensitive data out of exception messages and
 * custom diagnostic fields. The URL filter also applies to every
 * breadcrumb: the SDK records each API call and page change with its full
 * URL, and an address search, a picked place or a car search carries the
 * traveller's address in it.
 *
 * A failed API call keeps the X-Request-ID the API stamped on the response,
 * so an error the traveller sees can be quoted to support and matched to
 * one log line and one report on the server.
 */

let sdk = null;
let loading = null;

const stripQuery = (url) => (typeof url === "string" ? url.split(/[?#]/, 1)[0] : url);

// The URLs a breadcrumb keeps: a request's (xhr, fetch) and a page change's.
const BREADCRUMB_URLS = ["url", "from", "to"];

export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || !breadcrumb.data || typeof breadcrumb.data !== "object") return breadcrumb;
  const data = { ...breadcrumb.data };
  for (const key of BREADCRUMB_URLS) if (key in data) data[key] = stripQuery(data[key]);
  return { ...breadcrumb, data };
}

export function scrubEvent(event) {
  if (event && event.request) {
    event.request = { ...event.request, url: stripQuery(event.request.url), headers: {} };
    delete event.request.cookies;
    delete event.request.query_string;
    delete event.request.data;
  }
  if (event && event.user) {
    event.user = event.user.id ? { id: event.user.id } : {};
  }
  // Recorded before the scrub below existed, or by an integration that skips it.
  if (event && Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }
  return event;
}

/**
 * Start reporting when a DSN is configured. Resolves to whether it is on.
 * `importSentry` is the loader — injected by tests, the real dynamic import
 * otherwise, so the SDK is never touched when there is no DSN.
 */
export async function initObservability({
  dsn = process.env.REACT_APP_SENTRY_DSN,
  environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV,
  release = process.env.REACT_APP_RELEASE,
  importSentry = () => import("@sentry/react"),
} = {}) {
  if (!dsn) return false;
  if (sdk) return true;
  if (!loading) {
    loading = importSentry().then((module) => {
      module.init({
        dsn,
        environment,
        release: release || undefined,
        sendDefaultPii: false,
        tracesSampleRate: 0,
        beforeSend: scrubEvent,
        beforeBreadcrumb: scrubBreadcrumb,
      });
      sdk = module;
      return true;
    }).catch(() => {
      loading = null;
      return false;
    });
  }
  return loading;
}

/** Report an error if reporting is on; a no-op otherwise. Never throws. */
export function reportError(error, context = {}) {
  try {
    if (sdk) sdk.captureException(error, { extra: context });
  } catch {
    /* reporting must never be the thing that breaks */
  }
}

/** Keep the API's request id on a failed call, for the person who reads the toast. */
export function attachRequestId(error) {
  const id = error?.response?.headers?.["x-request-id"];
  if (id) error.requestId = id;
  return error;
}

/** Test seam: forget the loaded SDK. */
export function _reset() {
  sdk = null;
  loading = null;
}
